// ============================================================================
// SUPPORT TICKET DETAIL MODAL
// Simplified modal for support tickets - similar to finance document modal
// Shows only relevant tabs: Overview, Comments, Activity
// Supports both admin view (all comments) and customer view (public only)
// Date: December 2025
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-support/workflows/support-desk/ticket-detail.mdx
 * (published at user-docs.totalaccess.co.za/total-support/workflows/support-desk/ticket-detail)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Edit, MessageSquare, Activity, Clock, AlertCircle, CheckCircle, User, Flame, Save, Trash2, Paperclip, Upload, Download, Briefcase, ArrowRight, Loader2, Calendar, Link2, Monitor } from 'lucide-react';
import { authService } from '@/lib/authService';
import { useTheme } from '@/contexts/ThemeContext';
import type { WorkItem } from './UniversalWorkItems';
import { notify, confirm } from '@/lib/notifications';
import DetailsSidebar from './work-items/DetailsSidebar';
import { DualSLATimer } from './shared/SLATimer';
import ServiceWorkflowManager from './workflow/ServiceWorkflowManager';

// ============================================================================
// TYPES
// ============================================================================

interface SupportTicketDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: WorkItem | null;
  onRefresh?: () => void;
  viewMode?: 'admin' | 'customer'; // Admin sees all, customer sees public only
  onViewJob?: (jobId: string) => void; // Callback to view created job
}

interface Comment {
  id: string;
  user_id: number;
  user_name: string;
  comment_text: string;
  is_internal: boolean;
  created_at: string;
  updated_at: string;
}

interface ActivityEntry {
  id: string;
  user_id: number;
  user_name: string;
  action: string;
  field_changed?: string;
  old_value?: string;
  new_value?: string;
  details?: any;
  created_at: string;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

import { formatDateTimeSAST } from '@/lib/sastTime';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import UserDocsLink from '@/components/shared/UserDocsLink';

const formatTimeAgo = (dateString: string | null | undefined): string => {
  if (!dateString) return 'recently';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'recently';

  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;

  return formatDateTimeSAST(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ============================================================================
// COMMENTS TAB - Enhanced with Public Reply / Internal Note Buttons
// ============================================================================

function CommentsTab({ 
  ticketId, 
  isActive, 
  viewMode = 'admin' 
}: { 
  ticketId: string; 
  isActive: boolean; 
  viewMode?: 'admin' | 'customer';
}) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState<'all' | 'public' | 'internal'>('all');

  const loadComments = useCallback(async () => {
    if (!isActive) return;
    
    setLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(`/api/v1/work-items/${ticketId}/comments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to load comments');
      
      const data = await res.json();
      if (data.success) {
        // Filter comments based on view mode
        const filtered = viewMode === 'customer' 
          ? (data.data || data.comments || []).filter((c: Comment) => !c.is_internal)
          : (data.data || data.comments || []);
        setComments(filtered);
      }
    } catch (error: any) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  }, [ticketId, isActive, viewMode]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleAddComment = async () => {
    if (!newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(`/api/v1/work-items/${ticketId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          comment_text: newComment.trim(),
          is_internal: viewMode === 'admin' ? isInternal : false
        })
      });

      if (!res.ok) throw new Error('Failed to add comment');
      
      const data = await res.json();
      if (data.success) {
        setNewComment('');
        setIsInternal(false);
        notify.success('Comment added successfully');
        loadComments();
      }
    } catch (error: any) {
      notify.error(error.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
      </div>
    );
  }

  // Filter comments
  const filteredComments = (comments || []).filter(c => {
    if (filter === 'all') return true;
    if (filter === 'public') return !c.is_internal;
    if (filter === 'internal') return c.is_internal;
    return true;
  });

  // Count comments by type
  const publicCount = (comments || []).filter(c => !c.is_internal).length;
  const internalCount = (comments || []).filter(c => c.is_internal).length;

  return (
    <div className="flex flex-col h-full">
      {/* Add Comment Form - Top */}
      {viewMode === 'admin' && (
        <div className={`p-4 border-b border-gray-200 dark:border-gray-700 rounded-t-lg ${
          isInternal 
            ? 'bg-yellow-50 dark:bg-yellow-900/20' 
            : 'bg-blue-50 dark:bg-blue-900/20'
        }`}>
          {/* Comment Type Toggle - Prominent Buttons */}
          <div className="flex items-center gap-3 mb-3">
            <button
              type="button"
              onClick={() => setIsInternal(false)}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                !isInternal 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              Public Reply
            </button>
            <button
              type="button"
              onClick={() => setIsInternal(true)}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                isInternal 
                  ? 'bg-yellow-500 text-white shadow-md' 
                  : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Internal Note
            </button>
          </div>
          
          {/* Context hint */}
          <div className={`text-xs mb-2 px-2 py-1 rounded ${
            isInternal 
              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200' 
              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
          }`}>
            {isInternal 
              ? '🔒 Internal notes are only visible to your team - customers cannot see these' 
              : '👁️ Public replies are visible to customers and agents'}
          </div>
          
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={isInternal ? "Add internal note (team members only)..." : "Add public reply (customer will see this)..."}
            data-tour-id="ticket-reply-input"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
              isInternal ? 'border-yellow-400 dark:border-yellow-600 focus:ring-yellow-500' : 'border-blue-400 dark:border-blue-600 focus:ring-blue-500'
            }`}
            rows={3}
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={handleAddComment}
              disabled={submitting || !newComment.trim()}
              className={`px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center gap-2 ${
                isInternal ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {submitting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Adding...
                </>
              ) : (
                <>
                  {isInternal ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  ) : (
                    <MessageSquare className="w-4 h-4" />
                  )}
                  Add {isInternal ? 'Internal Note' : 'Public Reply'}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      {viewMode === 'admin' && comments && comments.length > 0 && (
        <div className="flex items-center gap-1 p-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            All ({comments.length})
          </button>
          <button
            onClick={() => setFilter('public')}
            className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
              filter === 'public'
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <MessageSquare className="w-3 h-3" />
            Public ({publicCount})
          </button>
          <button
            onClick={() => setFilter('internal')}
            className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
              filter === 'internal'
                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Internal ({internalCount})
          </button>
        </div>
      )}

      {/* Comments List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filteredComments.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>{filter === 'all' ? 'No comments yet. Be the first to comment!' : `No ${filter} comments.`}</p>
          </div>
        ) : (
          filteredComments.map((comment) => (
            <div
              key={comment.id}
              className={`border-l-4 rounded-lg p-4 border ${
                comment.is_internal
                  ? 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700'
                  : 'border-l-blue-500 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    comment.is_internal ? 'bg-yellow-100 dark:bg-yellow-900' : 'bg-blue-100 dark:bg-blue-900'
                  }`}>
                    <User className={`w-4 h-4 ${
                      comment.is_internal ? 'text-yellow-600 dark:text-yellow-400' : 'text-blue-600 dark:text-blue-400'
                    }`} />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900 dark:text-white">
                      {comment.user_name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDateTimeSAST(comment.created_at)}
                    </p>
                  </div>
                </div>
                {comment.is_internal && (
                  <span className="px-2 py-1 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 rounded border border-yellow-300 dark:border-yellow-700">
                    Internal
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {comment.comment_text}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================================
// ACTIVITY TAB - Shows full history from creation
// ============================================================================

function ActivityTab({ ticketId, isActive }: { ticketId: string; isActive: boolean }) {
  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'changes' | 'status' | 'comments'>('all');

  useEffect(() => {
    if (!isActive) return;

    const loadActivity = async () => {
      setLoading(true);
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) return;

        const res = await fetch(`/api/v1/work-items/${ticketId}/activity?limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            // Handle both data.activities and data.data response formats
            setActivities(data.activities || data.data || []);
          }
        }
      } catch (error) {
        console.error('Error loading activity:', error);
      } finally {
        setLoading(false);
      }
    };

    loadActivity();
  }, [ticketId, isActive]);

  const getActivityIcon = (action: string) => {
    switch (action) {
      case 'created': return { emoji: '🎉', color: 'text-green-500' };
      case 'updated': return { emoji: '✏️', color: 'text-blue-500' };
      case 'status_changed': return { emoji: '🔄', color: 'text-purple-500' };
      case 'assigned': return { emoji: '👤', color: 'text-indigo-500' };
      case 'unassigned': return { emoji: '👤', color: 'text-gray-500' };
      case 'commented': return { emoji: '💬', color: 'text-cyan-500' };
      case 'closed': case 'resolved': return { emoji: '✅', color: 'text-green-600' };
      case 'attachment': return { emoji: '📎', color: 'text-gray-500' };
      default: return { emoji: '📝', color: 'text-gray-500' };
    }
  };

  const getActivityDescription = (activity: ActivityEntry): string => {
    const { action, field_changed, old_value, new_value } = activity;
    
    if (action === 'created') return 'Ticket created';
    if (action === 'status_changed' || field_changed === 'status') {
      return `Status: ${old_value || 'none'} → ${new_value || 'unknown'}`;
    }
    if (action === 'assigned') return `Assigned to ${new_value || 'user'}`;
    if (action === 'unassigned') return `Unassigned ${old_value || 'user'}`;
    if (action === 'commented') return 'Added a comment';
    if (field_changed) {
      const fieldName = field_changed.replace(/_/g, ' ');
      if (old_value && new_value) {
        return `${fieldName}: "${old_value}" → "${new_value}"`;
      }
      return `Updated ${fieldName}`;
    }
    
    return 'Updated ticket';
  };

  // Filter activities
  const filteredActivities = (activities || []).filter(a => {
    if (filter === 'all') return true;
    if (filter === 'changes') return a.action === 'updated' || a.field_changed;
    if (filter === 'status') return a.action === 'status_changed' || a.field_changed === 'status';
    if (filter === 'comments') return a.action === 'commented';
    return true;
  });

  // Group activities by date in SAST
  const groupedActivities = filteredActivities.reduce((groups: Record<string, ActivityEntry[]>, activity) => {
    const date = new Date(activity.created_at);
    const day = date.getDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const dateStr = `${day} ${month} ${year}`;
    
    if (!groups[dateStr]) groups[dateStr] = [];
    groups[dateStr].push(activity);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Filter Bar */}
      <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
        {[
          { key: 'all', label: 'All' },
          { key: 'changes', label: 'Changes' },
          { key: 'status', label: 'Status' },
          { key: 'comments', label: 'Comments' }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key as any)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              filter === f.key
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
          {filteredActivities.length} entries
        </span>
      </div>

      {/* Activity Log */}
      <div className="flex-1 overflow-y-auto p-4">
        {Object.keys(groupedActivities).length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>No activity yet</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedActivities).map(([date, dayActivities]) => (
              <div key={date}>
                {/* Date Header */}
                <div className="sticky top-0 z-10 py-2 px-3 mb-2 text-xs font-semibold bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 rounded">
                  {date}
                </div>
                
                {/* Day's Activities */}
                <div className="space-y-2">
                  {dayActivities.map((activity) => {
                    const { emoji } = getActivityIcon(activity.action);
                    return (
                      <div
                        key={activity.id}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <span className="text-lg flex-shrink-0 mt-0.5">{emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 dark:text-white font-medium">
                            {activity.user_name || 'System'}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {getActivityDescription(activity)}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <Clock className="w-3 h-3" />
                            <span>{formatDateTimeSAST(activity.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// ATTACHMENTS TAB
// ============================================================================

interface Attachment {
  id: string;
  work_item_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  storage_path: string;
  uploaded_by: string;
  uploaded_at: string;
  download_url?: string;
}

function AttachmentsTab({ 
  ticketId, 
  isActive, 
  viewMode = 'admin' 
}: { 
  ticketId: string; 
  isActive: boolean; 
  viewMode?: 'admin' | 'customer';
}) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadAttachments = useCallback(async () => {
    if (!isActive) return;
    
    setLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(`/api/v1/work-items/${ticketId}/attachments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to load attachments');
      
      const data = await res.json();
      if (data.success) {
        setAttachments(data.data || data.attachments || []);
      }
    } catch (error: any) {
      console.error('Error loading attachments:', error);
    } finally {
      setLoading(false);
    }
  }, [ticketId, isActive]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });

      const res = await fetch(`/api/v1/work-items/${ticketId}/attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) throw new Error('Failed to upload files');
      
      const data = await res.json();
      if (data.success) {
        notify.success(`${files.length} file(s) uploaded successfully`);
        loadAttachments();
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    } catch (error: any) {
      notify.error(error.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachmentId: string) => {
    const shouldDelete = await confirm({
      title: 'Delete attachment',
      message: 'Are you sure you want to delete this attachment?',
      confirmText: 'Delete',
      variant: 'danger'
    });
    if (!shouldDelete) return;

    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(`/api/v1/work-items/${ticketId}/attachments/${attachmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to delete attachment');
      
      notify.success('Attachment deleted successfully');
      loadAttachments();
    } catch (error: any) {
      notify.error(error.message || 'Failed to delete attachment');
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');

      const downloadUrl = `/api/v1/work-items/${ticketId}/attachments/${attachment.id}/download`;
      const res = await fetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to download file');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      notify.error(error.message || 'Failed to download file');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('zip') || fileType.includes('rar')) return '📦';
    return '📎';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Upload Section - Admin only */}
      {viewMode === 'admin' && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            id="attachment-upload"
          />
          <label
            htmlFor="attachment-upload"
            className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
              uploading
                ? 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
                : 'border-blue-300 dark:border-blue-700 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
            }`}
          >
            {uploading ? (
              <>
                <svg className="w-5 h-5 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                <span className="text-sm text-gray-600 dark:text-gray-400">Uploading...</span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Click to upload files or drag and drop
                </span>
              </>
            )}
          </label>
        </div>
      )}

      {/* Attachments List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {attachments.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Paperclip className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>No attachments yet</p>
            {viewMode === 'admin' && (
              <p className="text-sm mt-2">Upload files to attach them to this ticket</p>
            )}
          </div>
        ) : (
          attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              {/* File Icon */}
              <span className="text-2xl flex-shrink-0">
                {getFileIcon(attachment.file_type)}
              </span>
              
              {/* File Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {attachment.file_name}
                </p>
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                  <span>{formatFileSize(attachment.file_size)}</span>
                  <span>•</span>
                  <span>{formatTimeAgo(attachment.uploaded_at)}</span>
                  {attachment.uploaded_by && (
                    <>
                      <span>•</span>
                      <span>by {attachment.uploaded_by}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownload(attachment)}
                  className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </button>
                {viewMode === 'admin' && (
                  <button
                    onClick={() => handleDelete(attachment.id)}
                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================================
// OVERVIEW TAB
// ============================================================================

function OverviewTab({ ticket }: { ticket: WorkItem }) {
  const getPriorityColor = (priority?: string | null) => {
    switch (priority?.toLowerCase()) {
      case 'urgent': return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
      case 'high': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusColor = (status?: string | null) => {
    switch (status?.toLowerCase()) {
      case 'resolved':
      case 'closed':
        return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
      case 'in-progress':
      case 'in_progress':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Determine if reporter is external or internal
  const isExternalReporter = ticket.contact_id && ticket.contact_name;
  const reporterName = isExternalReporter ? ticket.contact_name : (ticket.reporter_name || 'Not specified');
  const reporterType = isExternalReporter ? 'Customer' : 'Internal';

  return (
    <div className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
      {/* Title */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {ticket.title}
        </h3>
      </div>

      {/* Description */}
      {ticket.description && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description
          </label>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {ticket.description}
            </p>
          </div>
        </div>
      )}

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Status
          </label>
          <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(ticket.status)}`}>
            {ticket.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </span>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Priority
          </label>
          <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getPriorityColor(ticket.priority)}`}>
            {ticket.priority?.replace(/\b\w/g, l => l.toUpperCase())}
          </span>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Reporter
          </label>
          <div className="flex items-center gap-2">
            <p className="text-sm text-gray-900 dark:text-white">{reporterName}</p>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              isExternalReporter 
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
            }`}>
              {reporterType}
            </span>
          </div>
          {ticket.contact_location && (
            <p className="text-xs text-gray-500 mt-1">
              📍 {ticket.contact_location}
            </p>
          )}
        </div>

        {ticket.created_at && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Created
            </label>
            <p className="text-sm text-gray-900 dark:text-white">
              {new Date(ticket.created_at).toLocaleDateString()}
            </p>
          </div>
        )}

        {ticket.category && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <p className="text-sm text-gray-900 dark:text-white">{ticket.category}</p>
          </div>
        )}

        {ticket.subcategory && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Subcategory
            </label>
            <p className="text-sm text-gray-900 dark:text-white">{ticket.subcategory}</p>
          </div>
        )}
      </div>

      {/* Tags */}
      {ticket.tags && ticket.tags.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tags
          </label>
          <div className="flex flex-wrap gap-2">
            {ticket.tags.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function SupportTicketDetailModal({
  isOpen,
  onClose,
  ticket,
  onRefresh,
  viewMode = 'admin',
  onViewJob
}: SupportTicketDetailModalProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [showWorkflow, setShowWorkflow] = useState(false);
  const [linkedJob, setLinkedJob] = useState<WorkItem | null>(null);
  const [remoteAssistLoading, setRemoteAssistLoading] = useState(false);
  const { currentTheme, clientBranding, headerColor } = useTheme();

  // Load linked job if exists
  useEffect(() => {
    if (isOpen && ticket) {
      setActiveTab('overview');
      setShowWorkflow(false);
      
      // Check for linked job
      const loadLinkedJob = async () => {
        try {
          const token = authService.getTokens()?.access_token;
          if (!token) return;

          // Check if ticket has a linked job in custom_fields
          const linkedJobId = ticket.custom_fields?.linked_job_id;
          if (linkedJobId) {
            const res = await fetch(`/api/v1/work-items/${linkedJobId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
              const data = await res.json();
              if (data.success && data.data) {
                setLinkedJob(data.data);
              }
            }
          } else {
            // Also check relationships
            const res = await fetch(`/api/v1/work-items/${ticket.id}/relationships`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
              const data = await res.json();
              if (data.success && data.data?.relationships) {
                const incoming = data.data.relationships.incoming || {};
                // Look for jobs that were created_from this ticket
                Object.values(incoming).forEach((rels) => {
                  if (Array.isArray(rels)) {
                    rels.forEach((rel: { item?: WorkItem }) => {
                      if (rel.item?.item_type === 'job') {
                        setLinkedJob(rel.item);
                      }
                    });
                  }
                });
              }
            }
          }
        } catch (error) {
          console.error('Error loading linked job:', error);
        }
      };
      
      loadLinkedJob();
    }
  }, [isOpen, ticket]);

  if (!isOpen || !ticket) return null;

  const isDark = currentTheme?.mode === 'dark';
  const primaryColor = clientBranding?.primary_color || currentTheme?.colors?.primary || '#3b82f6';
  const headerBgColor = headerColor || clientBranding?.header_color || primaryColor;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Edit },
    { id: 'comments', label: 'Comments', icon: MessageSquare },
    { id: 'activity', label: 'Activity', icon: Activity },
    { id: 'attachments', label: 'Attachments', icon: Paperclip },
    ...(viewMode === 'admin' ? [{ id: 'workflow', label: 'Workflow', icon: ArrowRight }] : []),
  ];

  // Handle workflow transition (job created from ticket)
  const handleWorkflowTransition = (newItem: WorkItem, action: string) => {
    if (action === 'convertToJob') {
      setLinkedJob(newItem);
      setShowWorkflow(false);
      if (onRefresh) onRefresh();
    }
  };

  // Phase 10.3: Request Remote Assistance — creates a support session and
  // queues a remote_assist_request command on the user's device.
  const handleRequestRemoteAssist = async () => {
    if (!ticket) return;
    setRemoteAssistLoading(true);
    try {
      const tokens = authService.getTokens();
      const token = tokens?.access_token || '';
      // Create a support session
      const sessionResp = await fetch('/api/v1/support/sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          device_id: (ticket as Record<string, unknown>).device_id as number | null,
          ticket_id: ticket.id,
          initiated_by: 'technician',
          ttl_minutes: 30,
        }),
      });
      const sessionData = await sessionResp.json();
      if (!sessionData.success) {
        notify.error(sessionData.error || 'Failed to create support session');
        return;
      }
      // Queue the remote_assist_request command on the device
      if (sessionData.session.device_id) {
        await fetch(`/api/v1/devices/${sessionData.session.device_id}/remote-assist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            session_id: sessionData.session.id,
            room_name: sessionData.session.room_name,
            technician_name: 'Support Technician',
            ttl_minutes: 30,
          }),
        });
      }
      notify.success('Remote assistance request sent. Waiting for user approval...');
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to request remote assist');
    } finally {
      setRemoteAssistLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] isolate overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Container - Full screen */}
      <div className={`absolute inset-0 ${isDark ? 'bg-gray-900' : 'bg-white'} shadow-2xl overflow-hidden flex flex-col`} data-tour-id="ticket-detail-card">
        {/* Header */}
        <div 
          className="px-6 py-4 text-white flex items-center justify-between"
          style={{ backgroundColor: headerBgColor }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg">
              <Edit className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Service Ticket <UserDocsLink docsPath="/total-support/workflows/support-desk/ticket-detail" label="User guide: Ticket details" className="ml-1 align-middle text-white/80 hover:text-white" /></h2>
              <p className="text-sm text-white/80">
                #{ticket.item_number || ticket.id}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Linked Job Badge */}
            {linkedJob && (
              <button
                onClick={() => onViewJob?.(linkedJob.id)}
                className="flex items-center gap-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-medium transition-colors"
                title="View linked job"
              >
                <Briefcase className="w-4 h-4" />
                Job #{linkedJob.item_number}
              </button>
            )}
            
            {/* Convert to Job Button - Only show if no linked job and admin */}
            {viewMode === 'admin' && !linkedJob && (
              <button
                onClick={() => setShowWorkflow(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
              >
                <Briefcase className="w-4 h-4" />
                Convert to Job
              </button>
            )}
            
            {/* Phase 10.3: Request Remote Assistance — admin only */}
            {viewMode === 'admin' && (
              <button
                onClick={() => handleRequestRemoteAssist()}
                disabled={remoteAssistLoading}
                className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors"
                title="Request remote screen access to this user's device"
              >
                {remoteAssistLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Monitor className="w-4 h-4" />
                )}
                Remote Assist
              </button>
            )}
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* SLA Timers - Show if ticket has SLA config */}
        {(ticket.sla_first_response_deadline || ticket.sla_resolution_deadline) && (
          <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-red-50 dark:bg-red-900/20">
            <DualSLATimer
              firstResponseDeadline={ticket.sla_first_response_deadline ?? null}
              resolutionDeadline={ticket.sla_resolution_deadline ?? null}
              firstResponseBreached={ticket.sla_response_breached || false}
              resolutionBreached={ticket.sla_resolution_breached || false}
              firstResponseBreachedAt={ticket.sla_response_breached_at ?? null}
              resolutionBreachedAt={ticket.sla_resolution_breached_at ?? null}
              firstRespondedAt={ticket.sla_first_responded_at ?? ticket.first_responded_at ?? null}
              isPaused={ticket.status === 'pending' || ticket.status === 'waiting_on_customer'}
              variant="full"
            />
          </div>
        )}

        {/* Tabs */}
        <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content with Sidebar */}
        <div className="flex-1 overflow-hidden flex">
          {/* Main Content Area */}
          <div className="flex-1 overflow-hidden">
            {activeTab === 'overview' && <OverviewTab ticket={ticket} />}
            {activeTab === 'comments' && (
              <CommentsTab 
                ticketId={ticket.id} 
                isActive={activeTab === 'comments'} 
                viewMode={viewMode}
              />
            )}
            {activeTab === 'activity' && (
              <ActivityTab ticketId={ticket.id} isActive={activeTab === 'activity'} />
            )}
            {activeTab === 'attachments' && (
              <AttachmentsTab ticketId={ticket.id} isActive={activeTab === 'attachments'} viewMode={viewMode} />
            )}
            {activeTab === 'workflow' && viewMode === 'admin' && (
              <div className="p-4 overflow-y-auto h-full">
                <ServiceWorkflowManager
                  sourceItem={ticket}
                  onTransition={handleWorkflowTransition}
                />
                
                {/* Show linked job card if exists */}
                {linkedJob && (
                  <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 dark:bg-green-800 rounded-lg">
                          <Briefcase className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <p className="font-medium text-green-800 dark:text-green-200">
                            Linked Job #{linkedJob.item_number}
                          </p>
                          <p className="text-sm text-green-600 dark:text-green-400">
                            {linkedJob.title}
                          </p>
                          <p className="text-xs text-green-500 dark:text-green-500 mt-1">
                            Status: {linkedJob.status?.replace(/_/g, ' ')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => onViewJob?.(linkedJob.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium flex items-center gap-2"
                      >
                        View Job
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Details Sidebar - Admin only */}
          {viewMode === 'admin' && (
            <div className={`w-80 border-l ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'} overflow-y-auto`}>
              <DetailsSidebar
                workItem={ticket}
                onUpdate={async (updates) => {
                  try {
                    const token = authService.getTokens()?.access_token;
                    if (!token) throw new Error('Not authenticated');

                    const res = await fetch(`/api/v1/work-items/${ticket.id}`, {
                      method: 'PATCH',
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify(updates)
                    });

                    if (!res.ok) throw new Error('Failed to update ticket');
                    
                    notify.success('Ticket updated successfully');
                    if (onRefresh) onRefresh();
                  } catch (error: unknown) {
                    notify.error(error instanceof Error ? error.message : 'Failed to update ticket');
                    throw error;
                  }
                }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`border-t ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'} px-6 py-4 flex items-center justify-between`}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
          
          {/* Quick Actions */}
          {viewMode === 'admin' && !linkedJob && (
            <button
              onClick={() => {
                setActiveTab('workflow');
                setShowWorkflow(true);
              }}
              className="px-4 py-2 text-sm font-medium bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2"
            >
              <Briefcase className="w-4 h-4" />
              Convert to Service Job
            </button>
          )}
        </div>
        
        {/* Workflow Modal - Shows when Convert to Job clicked from header */}
        {showWorkflow && (
          <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/50">
            <div className={`w-full max-w-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl`}>
              <ServiceWorkflowManager
                sourceItem={ticket}
                onTransition={handleWorkflowTransition}
                onClose={() => setShowWorkflow(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
